
import React, { useState, useEffect } from 'react';
import { useReports } from '../contexts/ReportsContext';
import { useUser } from '../contexts/UserContext';
import { useQueue } from '../contexts/QueueContext';
import { useSipNumber } from '../contexts/SipNumberContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  LinearProgress,
  Zoom,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Phone as PhoneIcon,
  PhoneCallback as PhoneCallbackIcon,
  PhoneInTalk as PhoneInTalkIcon,
  PhoneMissed as PhoneMissedIcon,
  Timer as TimerIcon,
  AccessTime as AccessTimeIcon,
  Queue as QueueIcon,
  Home as HomeIcon,
  Assessment as AssessmentIcon,
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Settings as SettingsIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  QueueMusic as QueueMusicIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

// Types
interface Call {
  _id: string;
  phoneNumber: string;
  callerNumber?: string;
  calleeNumber?: string;
  customerName?: string;
  agent?: string;
  queue?: string;
  status: string;
  direction: string;
  startTime: string;
  answerTime?: string;
  endTime?: string;
  talkTime: number;
  waitTime: number;
  notes?: string;
  recording?: string;
  tags: string[];
  priority: string;
  sipNumber?: {
    extension: string;
  };
  createdAt: string;
}

const Dashboard: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reportsContext = useReports();
  const userContext = useUser();
  const queueContext = useQueue();
  const sipNumberContext = useSipNumber();

  const { users } = userContext;
  const { queues } = queueContext;
  const { sipNumbers } = sipNumberContext;

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        
        const [
          dailyStats,
          realTimeStats,
          activeAgents,
          waitingCalls,
          queuePerformance,
          hourlyDistribution
        ] = await Promise.all([
          reportsContext.fetchDailyStats(),
          reportsContext.fetchRealtimeStats(),
          reportsContext.fetchActiveAgents(),
          reportsContext.fetchWaitingCalls(),
          reportsContext.fetchQueuePerformance(),
          reportsContext.fetchHourlyDistribution()
        ]);

        // Transform data to match interface
        const transformedData = {
          dailyStats: {
            totalCalls: (dailyStats && dailyStats.totalCalls) || 0,
            outgoingCalls: (dailyStats && dailyStats.outgoingCalls) || 0,
            incomingCalls: (dailyStats && dailyStats.incomingCalls) || 0,
            missedCalls: (dailyStats && dailyStats.missedCalls) || 0
          },
          realTimeStats: {
            activeCalls: (realTimeStats && realTimeStats.activeCalls) || 0,
            waitingCalls: (realTimeStats && realTimeStats.waitingCalls) || 0,
            avgTalkTime: (realTimeStats && realTimeStats.avgTalkTime) || 0,
            avgWaitTime: (realTimeStats && realTimeStats.avgWaitTime) || 0
          },
          agents: (activeAgents || []).map((agent: any) => ({
            name: agent.name || 'Bilinmeyen',
            status: agent.status || 'unknown',
            statusTime: agent.statusTime || '0 dk',
            outgoingCalls: agent.outgoingCalls || 0,
            incomingCalls: agent.incomingCalls || 0
          })),
          waitingCalls: (waitingCalls || []).map((call: any) => ({
            caller: call.phoneNumber || 'Bilinmeyen',
            queue: call.queueName || 'Genel',
            waitTime: call.waitTime || '0:00',
            priority: call.priority || 'Normal'
          })),
          queues: (queuePerformance || []).map((queue: any) => ({
            name: queue.name || 'Bilinmeyen',
            totalCalls: queue.totalCalls || 0,
            answered: queue.answered || 0,
            missed: queue.missed || 0,
            avgWaitTime: queue.avgWaitTime || 0,
            agents: queue.agentCount || 0
          })),
          hourlyCalls: (hourlyDistribution || []).map((hour: any) => ({
            hour: hour.hour || '00:00',
            total: hour.total || 0,
            answered: hour.answered || 0,
            missed: hour.missed || 0
          }))
        };

        setDashboardData(transformedData);
      } catch (error) {
        console.error('Dashboard data loading error:', error);
        setError('Dashboard verileri yüklenemedi');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const [activeTab, setActiveTab] = useState('overview');
  const [activeCalls, setActiveCalls] = useState<Call[]>([]);

  // Real-time call polling
  useEffect(() => {
    // Call polling removed - using API data instead
  }, []);

  const menuItems = [
    { id: 'overview', label: 'Genel Bakış', icon: HomeIcon },
    { id: 'users', label: 'Kullanıcılar', icon: PeopleIcon },
    { id: 'sip', label: 'SIP Numaraları', icon: PhoneIcon },
    { id: 'queues', label: 'Kuyruklar', icon: QueueIcon },
    { id: 'calls', label: 'Çağrılar', icon: PhoneCallbackIcon },
    { id: 'reports', label: 'Raporlar', icon: AssessmentIcon },
    { id: 'settings', label: 'Ayarlar', icon: SettingsIcon }
  ];

  const quickActions = [
    { title: 'Kullanıcı Ekle', icon: PersonAddIcon, color: '#1976D2', path: '/admin/users/add' },
    { title: 'SIP Ekle', icon: PhoneIcon, color: '#4CAF50', path: '/admin/sip/add' },
    { title: 'Kuyruk Oluştur', icon: QueueMusicIcon, color: '#FF9800', path: '/admin/queues/add' },
    { title: 'Çağrıları İndir', icon: DownloadIcon, color: '#9C27B0', path: '/admin/calls/download' },
    { title: 'Raporlar', icon: AssessmentIcon, color: '#F44336', path: '/admin/reports' },
    { title: 'Ayarlar', icon: SettingsIcon, color: '#607D8B', path: '/admin/settings' }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'users':
        return renderUsers();
      case 'sip':
        return renderSip();
      case 'queues':
        return renderQueues();
      case 'calls':
        return renderCalls();
      case 'reports':
        return renderReports();
      case 'settings':
        return renderSettings();
      default:
        return renderOverview();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'success';
      case 'busy': return 'warning';
      case 'away': return 'info';
      default: return 'default';
    }
  };

  const renderOverview = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="700" color="#2C3E50" sx={{ mb: 3, fontFamily: 'Inter, sans-serif' }}>
        Admin Paneli - Genel Bakış
      </Typography>

      {/* İstatistik Kartları */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #1976D2 0%, #1565C0 100%)',
            color: 'white',
            minWidth: '200px',
            maxWidth: '100%'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Inter, sans-serif', wordBreak: 'break-all' }}>
                    {dashboardData.dailyStats.totalCalls || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontFamily: 'Inter, sans-serif' }}>
                    Toplam Çağrı
                  </Typography>
                </Box>
                <PhoneIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)',
            color: 'white'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Inter, sans-serif' }}>
                    {dashboardData.agents.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontFamily: 'Inter, sans-serif' }}>
                    Aktif Temsilci
                  </Typography>
                </Box>
                <GroupIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
            color: 'white'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Inter, sans-serif' }}>
                    {dashboardData.queues.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontFamily: 'Inter, sans-serif' }}>
                    Toplam Kuyruk
                  </Typography>
                </Box>
                <QueueIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
            color: 'white'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Inter, sans-serif' }}>
                    {dashboardData.realTimeStats.activeCalls}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, fontFamily: 'Inter, sans-serif' }}>
                    Aktif Çağrı
                  </Typography>
                </Box>
                <PhoneCallbackIcon sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Hızlı İşlemler */}
      <Typography variant="h5" fontWeight="600" color="#2C3E50" sx={{ mb: 2, fontFamily: 'Inter, sans-serif' }}>
        Hızlı İşlemler
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {quickActions.map((action, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
            <Card sx={{ 
              borderRadius: 2, 
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: '1px solid #E0E0E0',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
              }
            }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: action.color, width: 40, height: 40 }}>
                    <action.icon sx={{ color: 'white' }} />
                  </Avatar>
                  <Typography variant="body1" fontWeight="500" color="#2C3E50" sx={{ fontFamily: 'Inter, sans-serif' }}>
                    {action.title}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderUsers = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="700" color="#2C3E50" sx={{ mb: 3, fontFamily: 'Inter, sans-serif' }}>
        Kullanıcı Yönetimi
      </Typography>
      <Typography variant="body1" color="#666" sx={{ fontFamily: 'Inter, sans-serif' }}>
        Kullanıcı yönetimi sayfası - Yakında eklenecek
      </Typography>
    </Box>
  );

  const renderSip = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="700" color="#2C3E50" sx={{ mb: 3, fontFamily: 'Inter, sans-serif' }}>
        SIP Numara Yönetimi
      </Typography>
      <Typography variant="body1" color="#666" sx={{ fontFamily: 'Inter, sans-serif' }}>
        SIP numara yönetimi sayfası - Yakında eklenecek
      </Typography>
    </Box>
  );

  const renderQueues = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="700" color="#2C3E50" sx={{ mb: 3, fontFamily: 'Inter, sans-serif' }}>
        Kuyruk Yönetimi
      </Typography>
      <Typography variant="body1" color="#666" sx={{ fontFamily: 'Inter, sans-serif' }}>
        Kuyruk yönetimi sayfası - Yakında eklenecek
      </Typography>
    </Box>
  );

  const renderCalls = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="700" color="#2C3E50" sx={{ mb: 3, fontFamily: 'Inter, sans-serif' }}>
        Çağrı Yönetimi
      </Typography>
      <Typography variant="body1" color="#666" sx={{ fontFamily: 'Inter, sans-serif' }}>
        Çağrı yönetimi sayfası - Yakında eklenecek
      </Typography>
    </Box>
  );

  const renderReports = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="700" color="#2C3E50" sx={{ mb: 3, fontFamily: 'Inter, sans-serif' }}>
        Raporlar ve Analizler
      </Typography>
      <Typography variant="body1" color="#666" sx={{ fontFamily: 'Inter, sans-serif' }}>
        Raporlar sayfası - Yakında eklenecek
      </Typography>
    </Box>
  );

  const renderSettings = () => (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="700" color="#2C3E50" sx={{ mb: 3, fontFamily: 'Inter, sans-serif' }}>
        Sistem Ayarları
      </Typography>
      <Typography variant="body1" color="#666" sx={{ fontFamily: 'Inter, sans-serif' }}>
        Sistem ayarları sayfası - Yakında eklenecek
      </Typography>
    </Box>
  );

  const getStatusText = (status: string) => {
    switch (status) {
      case 'online': return 'Online';
      case 'busy': return 'Meşgul';
      case 'away': return 'Uzakta';
      default: return 'Offline';
    }
  };

  // const getPriorityColor = (priority: string) => { // Şu an kullanılmıyor
  //   switch (priority) {
  //     case 'Yüksek': return 'error';
  //     case 'Normal': return 'warning';
  //     case 'Düşük': return 'success';
  //     default: return 'default';
  //   }
  // };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Typography>Dashboard verileri yükleniyor...</Typography>
        </Box>
      ) : error ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Left Sidebar */}
      <Box
        sx={{
          width: 280,
          bgcolor: 'white',
          borderRight: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          height: '100vh',
          zIndex: 1000,
          top: 0,
        }}
      >
        {/* Logo Section */}
        <Box
          sx={{
            p: 3,
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: '#2C3E50'
          }}
        >
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'white',
              borderRadius: 2,
              color: '#2C3E50',
            }}
          >
            <AdminIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold" color="white">
              Admin Panel
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.7)">
              Dashboard
            </Typography>
          </Box>
        </Box>

        {/* Navigation Menu */}
        <Box sx={{ flex: 1, p: 2 }}>
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.id} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => setActiveTab(item.id)}
                  sx={{
                    borderRadius: 2,
                    bgcolor: activeTab === item.id ? '#E3F2FD' : 'transparent',
                    color: activeTab === item.id ? '#1976D2' : '#666',
                    '&:hover': {
                      bgcolor: activeTab === item.id ? '#E3F2FD' : '#F5F5F5'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <item.icon sx={{ 
                      color: activeTab === item.id ? '#1976D2' : '#666',
                      fontSize: 20
                    }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <Typography variant="body1" fontWeight={activeTab === item.id ? 600 : 400} sx={{ fontFamily: 'Inter, sans-serif' }}>
                        {item.label}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ marginLeft: '280px', overflow: 'auto' }}>
        <Fade in timeout={300}>
          {renderContent()}
        </Fade>
      </Box>


        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Zoom in timeout={900}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Gelen Arama
                  </Typography>
                    <Typography variant="h3" fontWeight="bold" color="info.main">
                      {dashboardData.dailyStats.incomingCalls}
                  </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Günlük
                    </Typography>
                  </Box>
                  <PhoneInTalkIcon sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
          </Zoom>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Zoom in timeout={1100}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Kaçan Çağrı
                  </Typography>
                    <Typography variant="h3" fontWeight="bold" color="error.main">
                      {dashboardData.dailyStats.missedCalls}
                  </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Günlük
                    </Typography>
                  </Box>
                  <PhoneMissedIcon sx={{ fontSize: 40, color: 'error.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Zoom>
      </Grid>

      {/* Second Row - Real-time Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Zoom in timeout={1300}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Aktif Çağrı
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="text.primary">
                      {dashboardData.realTimeStats.activeCalls}
                      </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Şu anda
                      </Typography>
                  </Box>
                  <PhoneInTalkIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>
                </CardContent>
              </Card>
          </Zoom>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Zoom in timeout={1500}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Bekleyen Çağrı
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="warning.main">
                      {dashboardData.realTimeStats.waitingCalls}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Kuyrukta
            </Typography>
                  </Box>
                  <TimerIcon sx={{ fontSize: 40, color: 'warning.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Zoom in timeout={1700}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Ort. Konuşma Süresi
                            </Typography>
                    <Typography variant="h3" fontWeight="bold" color="text.primary">
                      {dashboardData.realTimeStats.avgTalkTime}dk
                          </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Dakika
              </Typography>
                  </Box>
                  <AccessTimeIcon sx={{ fontSize: 40, color: 'success.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Zoom in timeout={1900}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Ort. Bekleme Süresi
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="text.primary">
                      {dashboardData.realTimeStats.avgWaitTime}dk
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Dakika
            </Typography>
                  </Box>
                  <TimerIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
      </Grid>

      {/* Tables Section */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        {/* Agents Table */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Zoom in timeout={2100}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ mb: 3 }}>
                  Temsilci Listesi
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Temsilci</TableCell>
                        <TableCell>Statü</TableCell>
                        <TableCell>Statü Süresi</TableCell>
                        <TableCell>Giden</TableCell>
                        <TableCell>Gelen</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dashboardData.agents.map((agent: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                {agent.name.split(' ').map((n: string) => n[0]).join('')}
                          </Avatar>
                              <Typography variant="body2" fontWeight="500">
                                {agent.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusText(agent.status)}
                              color={getStatusColor(agent.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {agent.statusTime}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {agent.outgoingCalls}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {agent.incomingCalls}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>

        {/* Waiting Calls Table */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Zoom in timeout={2300}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ mb: 3 }}>
                  Bekleyen Çağrılar
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Arayan</TableCell>
                        <TableCell>Kuyruk</TableCell>
                        <TableCell>Bekleme</TableCell>
                        <TableCell>Öncelik</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeCalls.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="text.secondary">
                              {loading ? '📞 Çağrılar yükleniyor...' : '📭 Aktif çağrı bulunamadı'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        activeCalls.map((call) => (
                          <TableRow key={call._id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="500">
                                {call.callerNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={call.sipNumber?.extension || call.calleeNumber || 'N/A'}
                                color="primary"
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="warning.main" fontWeight="500">
                                {new Date(call.createdAt).toLocaleTimeString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={call.status === 'ringing' ? 'Çalıyor' : call.status}
                                color={call.status === 'ringing' ? 'warning' : 'success'}
                                size="small" 
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Zoom>
        </Grid>
      </Grid>

      {/* Queue Details - 2 rows of 4 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {dashboardData.queues.slice(0, 4).map((queue: any, index: number) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Zoom in timeout={2500 + index * 200}>
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      {queue.name}
                    </Typography>
                    <QueueIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Toplam: {queue.totalCalls} | Cevaplanan: {queue.answered} | Kaçırılan: {queue.missed}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(queue.answered / queue.totalCalls) * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                    </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">
                      Ort. Bekleme: {queue.avgWaitTime}dk
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Temsilci: {queue.agents}
            </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
        ))}
      </Grid>

      {/* Second Row of Queues */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {dashboardData.queues.slice(4, 8).map((queue: any, index: number) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Zoom in timeout={3300 + index * 200}>
              <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                      {queue.name}
                            </Typography>
                    <QueueIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Toplam: {queue.totalCalls} | Cevaplanan: {queue.answered} | Kaçırılan: {queue.missed}
                          </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(queue.answered / queue.totalCalls) * 100}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">
                      Ort. Bekleme: {queue.avgWaitTime}dk
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Temsilci: {queue.agents}
              </Typography>
                  </Box>
              </CardContent>
            </Card>
            </Zoom>
        </Grid>
        ))}
      </Grid>

      {/* Hourly Calls Chart */}
      <Zoom in timeout={4100}>
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ mb: 3 }}>
              Saatlik Çağrı Dağılımı
            </Typography>
            
            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3, justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, backgroundColor: 'success.main', borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">Cevaplanan</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, backgroundColor: 'error.main', borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">Kaçırılan</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'end', gap: 1, height: 200, px: 2 }}>
              {dashboardData.hourlyCalls.map((hour: any, index: number) => {
                const maxCalls = Math.max(...dashboardData.hourlyCalls.map((h: any) => h.total));
                const answeredHeight = (hour.answered / maxCalls) * 100;
                const missedHeight = (hour.missed / maxCalls) * 100;
                
                return (
                  <Box key={index} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {/* Stacked Bar Chart */}
                    <Box sx={{ width: '100%', height: 120, display: 'flex', flexDirection: 'column', justifyContent: 'end', mb: 1 }}>
                      {/* Missed Calls (Red) */}
                      <Box
                        sx={{
                          width: '100%',
                          height: `${missedHeight}%`,
                          backgroundColor: 'error.main',
                          borderRadius: '2px 2px 0 0',
                          minHeight: missedHeight > 0 ? 2 : 0,
                          transition: 'all 0.3s ease'
                        }}
                      />
                      {/* Answered Calls (Green) */}
                      <Box
                        sx={{
                          width: '100%',
                          height: `${answeredHeight}%`,
                          backgroundColor: 'success.main',
                          borderRadius: missedHeight > 0 ? '0 0 0 0' : '2px 2px 0 0',
                          minHeight: answeredHeight > 0 ? 2 : 0,
                          transition: 'all 0.3s ease'
                        }}
                      />
                    </Box>
                    
                    {/* Hour Label */}
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                      {hour.hour}
                    </Typography>
                    
                    {/* Total Count */}
                    <Typography variant="caption" color="text.primary" fontWeight="500" sx={{ fontSize: '0.7rem' }}>
                      {hour.total}
                    </Typography>
                    
                    {/* Breakdown */}
                    <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                      <Typography variant="caption" color="success.main" sx={{ fontSize: '0.6rem', display: 'block' }}>
                        ✓{hour.answered}
                      </Typography>
                      <Typography variant="caption" color="error.main" sx={{ fontSize: '0.6rem', display: 'block' }}>
                        ✗{hour.missed}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      </Zoom>
        </>
      )}
    </Box>
  );
};

export default Dashboard;